import React, { useEffect, useRef, useState } from 'react';
import ProduccionScanInput from './ProduccionScanInput';
import { useAuth } from '../context/AuthContext';
import { produccionApi, errorMessage } from '../utils/produccionApi';

export const SEARCH_DELAY_MS = 250;
export const MIN_CHARS = 2;

// Finds a product by barcode, batch QR, code or name. Typing lists matches
// after a short pause; Enter (what the gun sends) searches at once and picks
// the exact match, or the only one.
function ProduccionSkuPicker({
  onSelect, placeholder = 'Escanea o escribe el producto', excludeIds = [], autoFocus = true,
}) {
  const { token } = useAuth();
  const [options, setOptions] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const [message, setMessage] = useState('');
  const [searching, setSearching] = useState(false);
  const latest = useRef(0);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const showOptions = (rows) => {
    setOptions(rows);
    setHighlight(-1);
  };

  const choose = (sku) => {
    showOptions([]);
    if (excludeIds.includes(sku.id)) {
      setMessage('Ese producto ya está en la lista.');
      return;
    }
    setMessage('');
    onSelect(sku);
  };

  const search = async (q, fromEnter) => {
    clearTimeout(timer.current);
    const request = latest.current + 1;
    latest.current = request;
    if (fromEnter) setSearching(true);
    const res = await produccionApi.get(`skus/?q=${encodeURIComponent(q)}`, token);
    if (request !== latest.current) return;
    setSearching(false);
    if (!res.ok) {
      showOptions([]);
      setMessage(errorMessage(res));
      return;
    }
    const rows = Array.isArray(res.data) ? res.data : [];
    const exact = fromEnter && rows.find((row) => row.exact);
    if (exact) choose(exact);
    else if (fromEnter && rows.length === 1) choose(rows[0]);
    else {
      showOptions(rows);
      setMessage(rows.length ? '' : `No se encontró ningún producto con «${q}».`);
    }
  };

  const handleType = (text) => {
    clearTimeout(timer.current);
    setMessage('');
    setHighlight(-1); // a new code is coming: Enter must search it
    const q = text.trim();
    if (q.length < MIN_CHARS) {
      latest.current += 1; // drops an answer still on its way
      showOptions([]);
      return;
    }
    timer.current = setTimeout(() => search(q, false), SEARCH_DELAY_MS);
  };

  const handleArrow = (step) => {
    const n = options.length;
    if (!n) return;
    setHighlight((h) => (h < 0 ? (step > 0 ? 0 : n - 1) : (h + step + n) % n));
  };

  // A camera read is always a code to look up, never "pick the highlighted row".
  const handleScan = (q, { camera = false } = {}) => {
    if (!camera && highlight >= 0 && options[highlight]) {
      clearTimeout(timer.current);
      choose(options[highlight]);
      return;
    }
    if (!q) return;
    showOptions([]);
    setMessage('');
    search(q, true);
  };

  return (
    <div className="prod-picker">
      <ProduccionScanInput
        placeholder={placeholder}
        onScan={handleScan}
        onType={handleType}
        onArrow={handleArrow}
        disabled={searching}
        autoFocus={autoFocus}
      />
      {message && <div className="prod-bad">{message}</div>}
      {options.length > 0 && (
        <ul className="prod-options" role="listbox" aria-label="Productos encontrados">
          {options.map((sku, index) => (
            <li key={sku.id} role="option" aria-selected={index === highlight}>
              <button
                type="button"
                className={`prod-option${index === highlight ? ' on' : ''}`}
                onClick={() => choose(sku)}
              >
                {`${sku.code} · ${sku.name}`}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ProduccionSkuPicker;
